import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  Building2,
  CalendarDays,
  Check,
  CircleAlert,
  Clock3,
  FileCheck2,
  FolderKanban,
  MapPin,
  Satellite,
} from "lucide-react"
import { useState } from "react"

import { OrderStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useDemoResetMaintenance } from "@/lib/demo-reset-status"
import type { OrderStatus } from "@/lib/domain"
import { ORDER_STATUS_LABELS, ORDER_STATUS_TRANSITIONS } from "@/lib/domain"
import { ordersQueryOptions, serviceQueryKeys } from "@/lib/query-options"
import { cn } from "@/lib/utils"
import { updateOrderStatusFn } from "@/server/functions"

const statusActions: Array<{
  status: Exclude<OrderStatus, "draft">
  label: string
  description: string
}> = [
  {
    status: "review",
    label: "Start review",
    description: "Validate area, use, sensor, and supporting context.",
  },
  {
    status: "approved",
    label: "Approve request",
    description: "Confirm the request is eligible for preparation.",
  },
  {
    status: "ready",
    label: "Mark ready",
    description: "Expose a synthetic release-ready package to the requester.",
  },
  {
    status: "completed",
    label: "Complete",
    description: "Close the request after its demo release step.",
  },
  {
    status: "rejected",
    label: "Needs revision",
    description: "Return the request with a visible correction state.",
  },
]

export const Route = createFileRoute("/admin/orders")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(ordersQueryOptions()),
  head: () => ({
    meta: [{ title: "Order queue · Service Portal admin" }],
  }),
  component: AdminOrdersPage,
})

function AdminOrdersPage() {
  const resetInProgress = useDemoResetMaintenance()
  const queryClient = useQueryClient()
  const { data } = useSuspenseQuery(ordersQueryOptions())
  const [selectedOrderId, setSelectedOrderId] = useState(
    data.orders.at(0)?.id ?? null
  )
  const [filter, setFilter] = useState<
    "All" | "Needs attention" | "Release ready"
  >("Needs attention")
  const [revisionReason, setRevisionReason] = useState(
    "Please clarify the programme use and narrow the requested coverage area."
  )
  const filteredOrders = data.orders.filter((order) => {
    if (filter === "Needs attention") {
      return ["submitted", "review", "approved"].includes(order.status)
    }
    if (filter === "Release ready") {
      return order.status === "ready"
    }
    return true
  })
  const selectedOrder =
    filteredOrders.find((order) => order.id === selectedOrderId) ??
    filteredOrders.at(0) ??
    null
  const statusMutation = useMutation({
    mutationFn: ({
      orderId,
      status,
      reason,
    }: {
      orderId: string
      status: Exclude<OrderStatus, "draft">
      reason?: string
    }) => updateOrderStatusFn({ data: { orderId, status, reason } }),
    onSuccess: async () => {
      setRevisionReason(
        "Please clarify the programme use and narrow the requested coverage area."
      )
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: serviceQueryKeys.orders() }),
        queryClient.invalidateQueries({ queryKey: serviceQueryKeys.admin() }),
        queryClient.invalidateQueries({
          queryKey: serviceQueryKeys.dashboard(),
        }),
      ])
    },
  })

  return (
    <div className="mx-auto max-w-[1480px]">
      <WorkspacePageHeader
        kicker="Operational review"
        title="Order queue"
        description="Review the exact synthetic requests visible in the analyst portal and advance them through production-shaped states."
      />

      <div className="mt-6 grid min-h-[730px] overflow-hidden rounded-[14px] border border-border bg-white xl:grid-cols-[420px_minmax(0,1fr)]">
        <aside className="border-b border-border xl:border-r xl:border-b-0">
          <div className="border-b border-border p-4">
            <div
              className="grid grid-cols-3 rounded-[10px] bg-[var(--cloud)] p-1"
              role="group"
              aria-label="Filter operational order queue"
            >
              {(["All", "Needs attention", "Release ready"] as const).map(
                (item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setFilter(item)}
                    className={cn(
                      "min-h-11 rounded-[8px] px-2 text-[0.68rem] font-semibold transition-colors focus-visible:outline-3 focus-visible:outline-[var(--cyan)]",
                      filter === item
                        ? "bg-white text-[var(--navy)] shadow-[0_4px_12px_-9px_rgba(18,58,90,.8)]"
                        : "text-muted-foreground hover:text-[var(--ink)]"
                    )}
                    aria-pressed={filter === item}
                  >
                    {item}
                  </button>
                )
              )}
            </div>
            <p className="mt-3 text-[0.65rem] text-muted-foreground">
              {filteredOrders.length} request
              {filteredOrders.length === 1 ? "" : "s"} in this view
            </p>
          </div>

          <div className="max-h-[650px] divide-y divide-border overflow-y-auto">
            {filteredOrders.map((order) => (
              <button
                key={order.id}
                type="button"
                onClick={() => setSelectedOrderId(order.id)}
                className={cn(
                  "w-full p-4 text-left transition-colors focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-[var(--cyan)]",
                  selectedOrder?.id === order.id
                    ? "bg-[#eef7f8]"
                    : "hover:bg-[var(--cloud)]"
                )}
                aria-pressed={selectedOrder?.id === order.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[var(--navy)]">
                      {order.title}
                    </span>
                    <span className="mt-1 block truncate text-[0.68rem] text-muted-foreground">
                      {order.agency}
                    </span>
                  </span>
                  <OrderStatusBadge status={order.status} />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="font-mono text-[0.6rem] text-[var(--telemetry)]">
                    {order.id}
                  </span>
                  <span className="text-[0.62rem] text-muted-foreground">
                    {order.updatedLabel}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="min-w-0 bg-[#fbfcfc]">
          {selectedOrder ? (
            <div className="grid min-h-full lg:grid-cols-[minmax(0,1fr)_330px]">
              <div className="min-w-0 p-5 sm:p-7">
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[0.68rem] font-semibold text-[var(--service-blue)]">
                        {selectedOrder.id}
                      </span>
                      <OrderStatusBadge status={selectedOrder.status} />
                    </div>
                    <h2 className="mt-3 text-3xl leading-[1.05] font-semibold tracking-[-0.03em] text-[var(--navy)]">
                      {selectedOrder.title}
                    </h2>
                    <p className="mt-3 max-w-[68ch] text-sm leading-6 text-muted-foreground">
                      {selectedOrder.purpose}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "grid size-12 shrink-0 place-items-center rounded-[12px]",
                      selectedOrder.kind === "Archive"
                        ? "bg-[#e8f4f7] text-[var(--service-blue)]"
                        : "bg-[#fff7df] text-[#6b5010]"
                    )}
                  >
                    {selectedOrder.kind === "Archive" ? (
                      <FolderKanban className="size-5" />
                    ) : (
                      <Satellite className="size-5" />
                    )}
                  </span>
                </div>

                <div className="mt-7 grid gap-4 rounded-[12px] border border-border bg-white p-4 sm:grid-cols-2">
                  <OperatorDetail
                    icon={Building2}
                    label="Requester and agency"
                    value={`${selectedOrder.requester} · ${selectedOrder.agency}`}
                  />
                  <OperatorDetail
                    icon={MapPin}
                    label="Area of interest"
                    value={selectedOrder.areaLabel}
                  />
                  <OperatorDetail
                    icon={Satellite}
                    label="Mission / request type"
                    value={`${selectedOrder.requestedSatellite} · ${selectedOrder.kind}`}
                  />
                  <OperatorDetail
                    icon={CalendarDays}
                    label="Capture window"
                    value={selectedOrder.dateRange}
                  />
                </div>

                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold text-[var(--navy)]">
                      Technical notes
                    </p>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {selectedOrder.notes}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[var(--navy)]">
                      Linked archive records
                    </p>
                    {selectedOrder.sceneIds.length > 0 ? (
                      <div className="mt-2 grid gap-2">
                        {selectedOrder.sceneIds.map((sceneId) => (
                          <p
                            key={sceneId}
                            className="rounded-[8px] bg-[#edf4f5] px-3 py-2 font-mono text-[0.65rem] text-[var(--ink)]"
                          >
                            {sceneId}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        New tasking has no archive scene attached. Acquisition
                        planning is the next operational step.
                      </p>
                    )}
                  </div>
                </div>

                {selectedOrder.operatorNote ? (
                  <div className="mt-6 rounded-[12px] border border-[#efc3c9] bg-[#fff5f6] p-4">
                    <p className="text-xs font-semibold text-[#8f2d3a]">
                      Operator revision note
                    </p>
                    <p className="mt-2 text-sm leading-6 text-[#6e4248]">
                      {selectedOrder.operatorNote}
                    </p>
                  </div>
                ) : null}

                {statusMutation.data?.id === selectedOrder.id ? (
                  <p
                    className="mt-6 rounded-[10px] border border-[#b8dcc8] bg-[#eaf6ef] px-4 py-3 text-sm text-[#246947]"
                    role="status"
                  >
                    Order updated to{" "}
                    {ORDER_STATUS_LABELS[statusMutation.data.status]}. The
                    analyst view now reflects this state.
                  </p>
                ) : null}
                {statusMutation.error ? (
                  <p
                    className="mt-6 rounded-[10px] bg-[#fff0f2] px-4 py-3 text-sm text-[#8f2d3a]"
                    role="alert"
                  >
                    {statusMutation.error.message}
                  </p>
                ) : null}
              </div>

              <aside className="border-t border-border bg-white p-5 lg:border-t-0 lg:border-l">
                <div className="flex items-center gap-2">
                  <FileCheck2 className="size-4 text-[var(--service-blue)]" />
                  <h3 className="text-xs font-semibold text-[var(--navy)]">
                    Update operational state
                  </h3>
                </div>
                <p className="mt-2 text-[0.68rem] leading-5 text-muted-foreground">
                  Changes are synthetic but shared immediately with the analyst
                  query cache.
                </p>

                {ORDER_STATUS_TRANSITIONS[selectedOrder.status].includes(
                  "rejected"
                ) ? (
                  <div className="mt-5 grid gap-2">
                    <Label htmlFor="revision-reason">Revision reason</Label>
                    <Textarea
                      id="revision-reason"
                      value={revisionReason}
                      onChange={(event) =>
                        setRevisionReason(event.target.value)
                      }
                      minLength={12}
                      maxLength={400}
                      rows={4}
                      aria-describedby="revision-reason-help"
                    />
                    <p
                      id="revision-reason-help"
                      className="text-[0.64rem] leading-5 text-muted-foreground"
                    >
                      Required when returning a request. The requester will see
                      this synthetic audit note.
                    </p>
                  </div>
                ) : null}

                <div className="mt-5 grid gap-2">
                  {statusActions
                    .filter((action) =>
                      ORDER_STATUS_TRANSITIONS[selectedOrder.status].includes(
                        action.status
                      )
                    )
                    .map((action) => {
                      const current = selectedOrder.status === action.status
                      const destructive = action.status === "rejected"
                      return (
                        <button
                          key={action.status}
                          type="button"
                          onClick={() =>
                            statusMutation.mutate({
                              orderId: selectedOrder.id,
                              status: action.status,
                              reason: destructive
                                ? revisionReason.trim()
                                : undefined,
                            })
                          }
                          disabled={
                            resetInProgress ||
                            statusMutation.isPending ||
                            current ||
                            (destructive && revisionReason.trim().length < 12)
                          }
                          className={cn(
                            "rounded-[10px] border p-3 text-left transition-colors focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[var(--cyan)] disabled:cursor-not-allowed disabled:opacity-55",
                            current
                              ? "border-[var(--service-blue)] bg-[#eef7f8]"
                              : destructive
                                ? "border-[#efc3c9] bg-white hover:bg-[#fff5f6]"
                                : "border-border bg-white hover:border-[#9eb6bf] hover:bg-[var(--cloud)]"
                          )}
                        >
                          <span className="flex items-center gap-2 text-xs font-semibold text-[var(--ink)]">
                            {action.status === "rejected" ? (
                              <CircleAlert className="size-3.5 text-[#a23744]" />
                            ) : current ? (
                              <Check className="size-3.5 text-[var(--action)]" />
                            ) : (
                              <Clock3 className="size-3.5 text-[var(--service-blue)]" />
                            )}
                            {action.label}
                          </span>
                          <span className="mt-1.5 block text-[0.64rem] leading-5 text-muted-foreground">
                            {action.description}
                          </span>
                        </button>
                      )
                    })}
                  {ORDER_STATUS_TRANSITIONS[selectedOrder.status].length ===
                  0 ? (
                    <p className="rounded-[10px] bg-[var(--cloud)] px-3 py-4 text-xs leading-5 text-muted-foreground">
                      This request has reached its terminal workflow state. No
                      further operator action is available.
                    </p>
                  ) : null}
                </div>
              </aside>
            </div>
          ) : (
            <div className="grid min-h-[600px] place-items-center p-8 text-center">
              <div>
                <FolderKanban className="mx-auto size-8 text-[var(--telemetry)]" />
                <p className="mt-3 text-sm text-muted-foreground">
                  No order is selected in this queue.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function OperatorDetail({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Building2
  label: string
  value: string
}) {
  return (
    <div className="flex gap-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-[9px] bg-[#e8f4f7] text-[var(--service-blue)]">
        <Icon className="size-4" />
      </span>
      <dl>
        <dt className="text-[0.62rem] text-muted-foreground">{label}</dt>
        <dd className="mt-1 text-xs leading-5 font-semibold text-[var(--ink)]">
          {value}
        </dd>
      </dl>
    </div>
  )
}
